import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { Link, Loader2, Plus, Upload } from "lucide-react";
import { useAction, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConvexError } from "convex/values";
import { Route as SettingsRoute } from "@/routes/_app/_auth/dashboard/_layout.settings.index";
import { User } from "~/types";
import { VIDEO_EVENTS } from "@/constants/analyticsEvents";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc.js";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useStripeConnectAccount } from "@/hooks";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

interface NewPostDialogProps {
  campaign: Partial<Doc<"campaigns">> | undefined;
  contract: Partial<Doc<"creatorContracts">> | undefined;
  refetchDashboardData: () => void;
  user: User | null | undefined;
}

export const NewPostDialog = ({
  campaign,
  contract,
  refetchDashboardData,
  user,
}: NewPostDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { track } = useMixpanel();
  const [url, setUrl] = useState("");
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState<"url" | "video">("url");
  const [organicViewsConfirmed, setOrganicViewsConfirmed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if user has social accounts connected (same logic as navigation)
  const hasSocials =
    user?.creatorSocials && Object.keys(user.creatorSocials).length > 0;

  // Check if this is the special campaign that allows all countries and manual transfer
  const SPECIAL_CAMPAIGN_ID = "nd7f46a8fdvef2b71s7m4tpgkn7kr5cg";
  const isSpecialCampaign = campaign?._id === SPECIAL_CAMPAIGN_ID;

  // Stripe Connect account validation (only for non-special campaigns)
  const {
    hasAccount,
    isLoading: isLoadingStripeAccount,
    isChecked: stripeAccountChecked,
    checkStripeConnectAccount,
  } = useStripeConnectAccount();

  // Auto-check Stripe Connect account for non-special campaigns
  useEffect(() => {
    if (
      !isSpecialCampaign &&
      !stripeAccountChecked &&
      !isLoadingStripeAccount
    ) {
      checkStripeConnectAccount();
    }
  }, [
    isSpecialCampaign,
    stripeAccountChecked,
    isLoadingStripeAccount,
    checkStripeConnectAccount,
  ]);

  const createPost = useAction(api.videos.addPartnershipVideo);
  const [isLoading, setIsLoading] = useState(false);

  // Query for approved unlinked video projects (only if video projects are required)
  const approvedUnlinkedProjects = useQuery(
    api.videos.getApprovedUnlinkedVideoProjects,
    contract?._id && campaign?.allowCreatorsToPostDirectlyToSocials === false
      ? { contractId: contract._id }
      : "skip",
  );

  const [selectedProjectId, setSelectedProjectId] = useState<
    Id<"creatorVideoProjects"> | undefined
  >(undefined);

  // Set upload mode to video when URL mode shouldn't be shown
  useEffect(() => {
    const shouldShowUrlMode =
      campaign?.allowCreatorsToPostDirectlyToSocials !== false ||
      (approvedUnlinkedProjects && approvedUnlinkedProjects.length > 0);

    if (!shouldShowUrlMode && uploadMode === "url") {
      setUploadMode("video");
    }
  }, [
    campaign?.allowCreatorsToPostDirectlyToSocials,
    approvedUnlinkedProjects,
    uploadMode,
  ]);

  // Video upload functionality
  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);
  const { mutateAsync: createProject } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.createCreatorVideoProject,
    ),
  });

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        const uploadResponse = uploaded[0].response as {
          storageId: Id<"_storage">;
        };

        const projectId = await createProject({
          contractId: contract?._id as Id<"creatorContracts">,
          accountType: "creator",
          videoStorageId: uploadResponse.storageId,
          title: newProjectTitle,
        });

        // Track video submission via upload
        track(VIDEO_EVENTS.VIDEO_SUBMITTED, {
          user_id: user?._id,
          contract_id: contract?._id,
          campaign_id: campaign?._id,
          campaign_name: campaign?.name,
          project_id: projectId,
          project_title: newProjectTitle,
          submission_method: "upload",
          timestamp: new Date().toISOString(),
        });

        toast({
          title: "Video project created",
          description: "Your video has been uploaded and is ready for review",
        });

        // Navigate to the partnership videos tab
        navigate({
          to: "/partnerships/$partnershipId",
          params: { partnershipId: contract?._id as string },
          search: { tab: "videos", projectId: projectId },
        });

        // Reset form
        setNewProjectTitle("");
        setUploadMode("url");
        refetchDashboardData();
      } catch (error) {
        console.error("Error creating video project:", error);
        toast({
          title: "Error creating video project",
          description: "There was an error creating your video project",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    },
    onUploadError: () => {
      toast({
        title: "Error uploading video",
        description: "There was an error uploading your video",
        variant: "destructive",
      });
      setIsUploading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    // Validate social accounts are connected for URL submissions
    if (!hasSocials) {
      toast({
        title: "Social accounts required",
        description:
          "Please connect your social media accounts in Settings before submitting a published post URL",
        variant: "destructive",
      });
      return;
    }

    // Validate Stripe Connect for non-special campaigns
    if (!isSpecialCampaign && !hasAccount) {
      toast({
        title: "Payment account required",
        description:
          "Please set up your Stripe Connect account in Billing Settings before submitting a published post",
        variant: "destructive",
      });
      return;
    }

    if (!organicViewsConfirmed) {
      toast({
        title: "Confirmation required",
        description:
          "Please confirm that your post will not be ads boosted and all views will be organic",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      // Use createPost with optional videoProjectId parameter
      await createPost({
        url,
        contractId: contract?._id as Id<"creatorContracts">,
        ...(selectedProjectId && { videoProjectId: selectedProjectId }),
      });

      // Track video submission
      track(VIDEO_EVENTS.VIDEO_SUBMITTED, {
        user_id: user?._id,
        contract_id: contract?._id,
        campaign_id: campaign?._id,
        campaign_name: campaign?.name,
        video_url: url,
        submission_method: "url",
        linked_to_project: !!selectedProjectId,
        project_id: selectedProjectId,
        timestamp: new Date().toISOString(),
      });

      if (selectedProjectId) {
        toast({
          title: "Project linked successfully",
          description:
            "Your approved video project has been linked to the published video",
        });
      }

      // Reset form
      setUrl("");
      setUploadMode("url");
      setOrganicViewsConfirmed(false);
      setSelectedProjectId(undefined);
      refetchDashboardData();
    } catch (error) {
      console.error("Failed to create post:", error);
      toast({
        title: selectedProjectId
          ? "Failed to link project"
          : "Failed to create post",
        description:
          error instanceof ConvexError
            ? error.data
            : "An error occurred, please make sure you own this content and check if you've linked the social media account to your profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVideoUpload = (files: File[]) => {
    if (!newProjectTitle.trim()) {
      toast({
        title: "Missing title",
        description: "Please provide a title for your video project",
        variant: "destructive",
      });
      return;
    }

    if (files.length === 0) {
      toast({
        title: "No video selected",
        description: "Please select a video file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    startUpload(files);
  };

  const handleStripeConnectCheck = async () => {
    try {
      const result = await checkStripeConnectAccount();
      return result;
    } catch (error) {
      console.error("Error checking Stripe Connect account:", error);
      return { hasAccount: false, account: null };
    }
  };

  // Check if campaign is active
  const isCampaignActive = campaign?.status === "active";
  const isInfluencerCampaign =
    campaign?.type === "influencer" ||
    campaign?.allowCreatorsToPostDirectlyToSocials === false;

  // Determine if we should show URL input for linking to approved projects
  const hasApprovedUnlinkedProjects =
    campaign?.allowCreatorsToPostDirectlyToSocials === false &&
    approvedUnlinkedProjects &&
    approvedUnlinkedProjects.length > 0;

  // For campaigns requiring video projects, show URL input only if there are approved unlinked projects
  const shouldShowUrlMode =
    campaign?.allowCreatorsToPostDirectlyToSocials !== false ||
    hasApprovedUnlinkedProjects;

  // Determine if user can upload videos without prerequisites
  // (for campaigns requiring video projects, they can upload videos for approval without social/stripe)
  const canUploadWithoutPrerequisites =
    campaign?.allowCreatorsToPostDirectlyToSocials === false &&
    !shouldShowUrlMode;

  if (!contract || !campaign || !isCampaignActive) {
    return (
      <button
        className={cn(
          "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm transition-all hover:shadow-md",
          "flex items-center gap-2 cursor-not-allowed opacity-50",
        )}
        disabled
        title={
          !contract || !campaign
            ? "You need to be working on an active campaign to create a post"
            : "Campaign has ended - cannot add new posts"
        }
      >
        <Plus className="h-4 w-4" />
        Add New Post
      </button>
    );
  }

  // Only check prerequisites if not in upload-only mode
  if (!canUploadWithoutPrerequisites) {
    // Check if user has social accounts connected - if not, redirect to settings
    if (!hasSocials) {
      return (
        <Dialog>
          <DialogTrigger asChild>
            <button
              className={cn(
                "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm transition-all hover:shadow-md",
                "flex items-center gap-2",
              )}
            >
              <Plus className="h-4 w-4" />
              Add New Post
            </button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Connect Social Media Accounts</DialogTitle>
              <DialogDescription>
                You need to connect your social media accounts before you can
                add posts. This helps us verify your content and track
                performance.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 flex flex-col gap-4 text-center">
              <div className="rounded-lg border border-border bg-secondary/20 p-4">
                <div className="mb-3">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                    <svg
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-primary">
                  No Social Accounts Connected
                </h3>
                <p className="mt-1 text-xs text-primary/60">
                  Connect your social media accounts to start creating posts and
                  tracking performance.
                </p>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button
                onClick={() => {
                  navigate({ to: SettingsRoute.fullPath });
                }}
                className="w-full"
              >
                Go to Settings
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }

    // For non-special campaigns, check Stripe Connect account setup
    if (!isSpecialCampaign) {
      // If we haven't checked the Stripe account yet, or if there's an issue, show validation dialog
      const shouldShowValidation = !stripeAccountChecked || !hasAccount;

      if (shouldShowValidation) {
        return (
          <Dialog>
            <DialogTrigger asChild>
              <button
                className={cn(
                  "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm transition-all hover:shadow-md",
                  "flex items-center gap-2",
                )}
              >
                <Plus className="h-4 w-4" />
                Add New Post
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Stripe Connect Account Required</DialogTitle>
                <DialogDescription>
                  You need to set up a Stripe Connect account before you can add
                  posts. This ensures you can receive earnings from your
                  content.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 flex flex-col gap-4 text-center">
                <div className="rounded-lg border border-border bg-secondary/20 p-4">
                  <div className="mb-3">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/20">
                      <svg
                        className="h-6 w-6 text-orange-600 dark:text-orange-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                    </div>
                  </div>
                  {!stripeAccountChecked ? (
                    <>
                      <h3 className="text-sm font-medium text-primary">
                        Checking Payment Setup...
                      </h3>
                      <p className="mt-1 text-xs text-primary/60">
                        Please wait while we verify your payment account.
                      </p>
                    </>
                  ) : !hasAccount ? (
                    <>
                      <h3 className="text-sm font-medium text-primary">
                        No Payment Account
                      </h3>
                      <p className="mt-1 text-xs text-primary/60">
                        Set up a Stripe Connect account to receive payments for
                        your content performance.
                      </p>
                    </>
                  ) : null}
                </div>
              </div>

              <DialogFooter className="mt-4">
                {!stripeAccountChecked ? (
                  <Button
                    onClick={handleStripeConnectCheck}
                    disabled={isLoadingStripeAccount}
                    className="w-full"
                  >
                    {isLoadingStripeAccount ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "Check Payment Setup"
                    )}
                  </Button>
                ) : !hasAccount ? (
                  <Button
                    onClick={() => {
                      navigate({ to: "/dashboard/settings/billing" });
                    }}
                    className="w-full"
                  >
                    Go to Billing Settings
                  </Button>
                ) : null}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      }
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className={cn(
            "bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm transition-all hover:shadow-md",
            "flex items-center gap-2",
          )}
        >
          <Plus className="h-4 w-4" />
          Add New Post
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Post</DialogTitle>
          <DialogDescription>
            {hasApprovedUnlinkedProjects
              ? "Link a published post URL to your approved video project, or upload a new video for approval."
              : isInfluencerCampaign
                ? "Upload a video for approval before posting."
                : "Add details about your new content post."}
          </DialogDescription>
        </DialogHeader>

        {/* Mode Toggle for Influencer Campaigns */}
        {isInfluencerCampaign && shouldShowUrlMode && (
          <div className="flex rounded-lg border border-input p-1">
            <button
              type="button"
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${uploadMode === "url"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
                }`}
              onClick={() => setUploadMode("url")}
            >
              <Link className="h-4 w-4 mr-2 inline" />
              {hasApprovedUnlinkedProjects
                ? "Link to Project"
                : "Published Post"}
            </button>
          </div>
        )}

        {uploadMode === "url" ? (
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
            {/* Project Selection for Approved Unlinked Projects */}
            {hasApprovedUnlinkedProjects && (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="project"
                  className="text-sm font-medium text-primary"
                >
                  Select Approved Video to Link
                </label>
                <select
                  id="project"
                  value={selectedProjectId || ""}
                  onChange={(e) =>
                    setSelectedProjectId(
                      e.target.value as Id<"creatorVideoProjects"> | undefined,
                    )
                  }
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                  required
                >
                  <option value="">Choose a video...</option>
                  {approvedUnlinkedProjects.map(
                    (project: {
                      _id: Id<"creatorVideoProjects">;
                      title: string;
                    }) => (
                      <option key={project._id} value={project._id}>
                        {project.title}
                      </option>
                    ),
                  )}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="url" className="text-sm font-medium text-primary">
                Published Post URL
              </label>
              <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                <Link className="h-4 w-4 text-muted-foreground" />
                <input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  required
                />
              </div>
            </div>

            {/* 7-Day View Counting Notice */}
            <div className="rounded-lg border border-border bg-secondary/20 p-3">
              <div className="flex items-start gap-2">
                <svg
                  className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                <div className="text-sm">
                  <p className="font-medium text-primary">
                    Views are counted for 7 days only
                  </p>
                  <p className="text-primary/60 mt-1">
                    View counts and related earnings will only be tracked for
                    the first 7 days after posting. Make sure to post when you
                    can maximize initial engagement.
                  </p>
                </div>
              </div>
            </div>

            {/* Organic Views Confirmation */}
            <div className="flex items-start space-x-2">
              <Checkbox
                id="organic-views-url"
                checked={organicViewsConfirmed}
                onCheckedChange={(checked) =>
                  setOrganicViewsConfirmed(checked === true)
                }
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="organic-views-url"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I confirm that this post will not be ads boosted. All views
                  must be organic. Any form of ad boosting, fake views or bot
                  activity will disqualify my entry.{" "}
                  <a
                    href="https://creatorgrid.io/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    (See https://creatorgrid.io/terms for more information about
                    our T&Cs)
                  </a>
                </label>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button
                type="submit"
                disabled={
                  !url ||
                  isLoading ||
                  !organicViewsConfirmed ||
                  (hasApprovedUnlinkedProjects && !selectedProjectId)
                }
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Add Post
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                Project Title
              </label>
              <input
                type="text"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background"
                placeholder="e.g., App Feature Showcase"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                Upload Video
              </label>
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-input p-6 bg-background">
                <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
                <p className="mb-1 text-sm font-medium">Upload your video</p>
                <p className="mb-4 text-xs text-muted-foreground">
                  {!newProjectTitle.trim()
                    ? "Enter a project title to continue"
                    : "Select a video file to upload for review"}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      handleVideoUpload(files);
                    }
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || !newProjectTitle.trim()}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Select Video
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
